import DebouncedA11yAnnouncer from '../../../../../common/components/A11yAnnouncer/DebouncedA11yAnnouncer';
import { useAppSelector } from '../../../../../common/hooks/useThunkDispatch';
import { useIsUserAMember } from '../../../../../common/hooks/useUserProfile';
import type { PriceType } from '../../../../../common/models/priceData/priceData';
import type { ProductVariantProps } from '../../../../../common/models/props/itemTeaserProps';
import { featureFlagsSelectors } from '../../../../../common/selectors/featureFlagsSelectors';
import { useIsB2BRoute } from '../../../../../common/utils/b2bUtils';
import type { ProductDetailsData } from '../ProductDetailsPage.types';
import ProductDetailsPriceLabels, {
    getProductDetailsPriceLabelA11yText,
} from './ProductDetailsPriceLabels';
import styles from './ProductDetailsPrices.module.scss';

const ProductDetailsPrices = ({
    product,
    variant,
    priceType,
}: {
    product: ProductDetailsData;
    variant: ProductVariantProps | undefined;
    priceType: PriceType;
}) => {
    const isUserMember = useIsUserAMember();

    const isB2B = useIsB2BRoute();
    const arePILPricesEnabled = useAppSelector(featureFlagsSelectors.selectPILPricesEnabled);
    const isPILPricesLogicEnabled = useAppSelector(
        featureFlagsSelectors.selectPILPricesLogicEnabled,
    );

    const primaryPriceLabel = getProductDetailsPriceLabelA11yText({
        type: 'primary',
        product,
        variant,
        priceType,
        a11yTextType: 'ariaHidden',
        isUserMember,
        isPILPricesLogicEnabled,
        isB2B,
        arePILPricesEnabled,
    });

    const secondaryPricesLabel = getProductDetailsPriceLabelA11yText({
        type: 'secondary',
        product,
        variant,
        isB2B,
        priceType,
        a11yTextType: 'ariaHidden',
        isUserMember,
        arePILPricesEnabled,
        isPILPricesLogicEnabled,
    });

    const a11yText = [primaryPriceLabel, secondaryPricesLabel].filter(Boolean).join(', ');

    const hasVariants = product.variants && product.variants.length > 0;

    return (
        <>
            {primaryPriceLabel && hasVariants && (
                <DebouncedA11yAnnouncer
                    text={primaryPriceLabel}
                    debounceTime={300}
                    announcerName="ProductDetailsPage variant selector"
                />
            )}
            <div className={styles.PricesContainer}>
                <div className={styles.PrimaryPricesContainer}>
                    <ProductDetailsPriceLabels
                        type="primary"
                        product={product}
                        variant={variant}
                        priceType={priceType}
                        a11yTextType="ariaHidden"
                        isUserMember={isUserMember}
                        isPILPricesLogicEnabled={isPILPricesLogicEnabled}
                        arePILPricesEnabled={arePILPricesEnabled}
                        isB2B={isB2B}
                    />
                </div>
                <p className="u-hiddenVisuallyOutlined">{a11yText}</p>
                <div className={styles.SecondaryPricesContainer}>
                    <ProductDetailsPriceLabels
                        type="secondary"
                        product={product}
                        variant={variant}
                        priceType={priceType}
                        a11yTextType="ariaHidden"
                        isUserMember={isUserMember}
                        isPILPricesLogicEnabled={isPILPricesLogicEnabled}
                        arePILPricesEnabled={arePILPricesEnabled}
                        isB2B={isB2B}
                    />
                </div>
            </div>
        </>
    );
};

export default ProductDetailsPrices;
